import { Events } from 'src/constants/webSocketConsts/events';
import {
  BuilderMessage,
  builderSessionApi,
  ListBuilderMessagesResponse,
  mergeBuilderMessages,
} from 'src/services/api/builderSessionApi';
import { AppDispatch } from 'src/store/configureStore';

const isBuilderMessage = (item: unknown): item is BuilderMessage =>
  typeof item === 'object' &&
  item !== null &&
  typeof (item as { id?: unknown }).id === 'string' &&
  typeof (item as { builderSessionId?: unknown }).builderSessionId === 'string';

/**
 * Group incoming WS items by builderSessionId. The dispatcher delivers items
 * for the same entity type in a single batch, but they may target multiple
 * sessions during fan-out (e.g. when an admin watches several sessions). One
 * cache update per session avoids redundant re-renders.
 */
const groupByBuilderSession = (
  items: BuilderMessage[],
): Map<string, BuilderMessage[]> => {
  const grouped = new Map<string, BuilderMessage[]>();
  for (const message of items) {
    const bucket = grouped.get(message.builderSessionId);
    if (bucket) {
      bucket.push(message);
    } else {
      grouped.set(message.builderSessionId, [message]);
    }
  }
  return grouped;
};

export function handleBuilderMessageUpdate(
  event: Events,
  items: unknown[],
  dispatch: AppDispatch,
) {
  if (event !== Events.Insert && event !== Events.Modify) {
    return;
  }

  const messages = items.filter(isBuilderMessage);
  if (messages.length === 0) {
    return;
  }

  // updateQueryData matches against the exact serialized query args. Patching
  // with bare { builderSessionId } only updates the cache entry for callers
  // that subscribe with the same shape — consumers that pass limit/nextToken
  // for pagination must hydrate from the GET response and accept that live WS
  // updates land on the unparameterized cache entry only.
  const grouped = groupByBuilderSession(messages);
  for (const [builderSessionId, sessionMessages] of grouped) {
    dispatch(
      builderSessionApi.util.updateQueryData(
        'listBuilderSessionMessages',
        { builderSessionId },
        (draft: ListBuilderMessagesResponse) => {
          draft.data = mergeBuilderMessages(draft.data ?? [], sessionMessages);
        },
      ),
    );
  }
}
